import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Paper,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Favorite as FavoriteIcon,
  Topic as TopicIcon,
  AutoFixHigh as AutoFixHighIcon,
  Tune as TuneIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useCompany } from '../hooks/useAuth';
import { PostService } from '../services/postService';
import { AiService, AiReviewResult } from '../services/aiService';
import { ThemeService } from '../services/themeService';
import { Theme } from '../types/theme';
import { AppColors } from '../theme/colors';
import { getBoxTemplate } from '../constants/industryTemplates';

interface PostFormContentProps {
  onPostCreated: () => void;
  onCancel?: () => void;
  onContentChange?: (hasContent: boolean) => void;
  onSubmissionStateChange?: (isSubmitting: boolean, progress: number, error: string | null, content?: string, isSubmissionStarted?: boolean) => void;
  showSubmissionDialog?: boolean;
  onCancelSubmissionRef?: React.MutableRefObject<(() => void) | null>;
  onCancelSubmission?: () => void;
  onSnackbarChange?: (open: boolean, message: string, severity: 'success' | 'error') => void;
  initialPostType?: 'voice' | 'thanks' | 'theme';
  showPostTypeSelector?: boolean;
  onPostTypeChange?: (type: 'voice' | 'thanks' | 'theme') => void;
}

export const PostFormContent: React.FC<PostFormContentProps> = ({
  onPostCreated,
  onCancel,
  onContentChange,
  onSubmissionStateChange,
  showSubmissionDialog = true,
  onCancelSubmissionRef,
  onCancelSubmission,
  onSnackbarChange,
  initialPostType = 'voice',
  showPostTypeSelector = true,
  onPostTypeChange,
}) => {
  const { user } = useAuth();
  const { company } = useCompany(user?.uid || null);

  // 新規投稿フォームの状態
  const [content, setContent] = useState('');
  const [selectedPostType, setSelectedPostType] = useState(initialPostType);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);

  // 二重送信を防ぐための鍵。連打の2回目のクリックは state の再描画より先に
  // 届くため、ボタンの disabled（state 依存）だけでは止められない。
  // isPreparing / isSubmitting と同じ意味を ref でも持つ。
  const preparingRef = useRef(false);
  const submittingRef = useRef(false);

  const updateSubmitting = (value: boolean) => {
    submittingRef.current = value;
    setIsSubmitting(value);
  };

  const [isReviewed, setIsReviewed] = useState(false);
  const [aiAdjustedPercent, setAiAdjustedPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);
  
  // スナックバーの状態
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  // テーマ関連の状態
  const [activeTheme, setActiveTheme] = useState<Theme | null>(null);
  const [themeLoading, setThemeLoading] = useState(false);
  
  // AI校閲関連の状態
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewResult, setReviewResult] = useState<AiReviewResult | null>(null);
  const [preflightDialogOpen, setPreflightDialogOpen] = useState(false);
  const [preflightIssues, setPreflightIssues] = useState<any[]>([]);
  
  // 投稿確認ダイアログの状態
  const [submissionDialogOpen, setSubmissionDialogOpen] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmissionStarted, setIsSubmissionStarted] = useState(false);
  const isCancelledRef = useRef(false);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  const aiService = AiService.getInstance();

  // アクティブテーマを取得
  const fetchActiveTheme = async () => {
    if (!company?.id) {
      return;
    }
    
    setThemeLoading(true);
    try {
      const theme = await ThemeService.getActiveTheme(company.id);
      setActiveTheme(theme);
    } catch (error) {
      console.error('PostFormContent: アクティブテーマ取得エラー詳細:', error);
    } finally {
      setThemeLoading(false);
    }
  };

  // テーマボックス選択時にアクティブテーマを取得
  useEffect(() => {
    if (selectedPostType === 'theme' && company?.id) {
      fetchActiveTheme();
    }
  }, [selectedPostType, company?.id]);

  // ルーティング（/post/new?type=...）等で初期タイプが変わった場合に同期する
  useEffect(() => {
    setSelectedPostType(initialPostType);
  }, [initialPostType]);

  // 内容変更を親に通知
  useEffect(() => {
    if (onContentChange) {
      onContentChange(content.trim().length > 0);
    }
  }, [content, onContentChange]);

  const getPlaceholderText = () => {
    switch (selectedPostType) {
      case 'voice':
        return '意見や提案を自由に書いてください...';
      case 'thanks':
        return '感謝の気持ちを伝えましょう...';
      case 'theme':
        return 'テーマについて自由に投稿してください...';
      default:
        return '内容を入力してください...';
    }
  };

  const getSelectedBoxName = () =>
    getBoxTemplate(company?.industryType, selectedPostType).name;

  const getPostTypeIcon = (type: string) => {
    switch (type) {
      case 'voice':
        return CampaignIcon;
      case 'thanks':
        return FavoriteIcon;
      case 'theme':
        return TopicIcon;
      default:
        return CampaignIcon;
    }
  };

  const getPostTypeColor = (type: string) => {
    switch (type) {
      case 'voice':
        return AppColors.voiceBoxColor;
      case 'thanks':
        return AppColors.thanksBoxColor;
      case 'theme':
        return AppColors.themeBoxColor;
      default:
        return AppColors.voiceBoxColor;
    }
  };

  const handleSubmit = async () => {
    if (!content.trim() || !user || !company) return;
    // AIリスク評価を待つ数秒のあいだに連打されると投稿が複数作られるため、
    // 押した時点で鍵をかける。
    if (preparingRef.current || submittingRef.current) return;
    preparingRef.current = true;
    setIsPreparing(true);
    try {
      await handleSubmitWithPreflight();
    } finally {
      preparingRef.current = false;
      setIsPreparing(false);
    }
  };

  const handleSubmitWithPreflight = async () => {
    if (!content.trim() || !user || !company) return;

    try {
      // AIリスク評価を実行
      const riskAssessment = await aiService.riskAssess(content, selectedPostType);
      
      if (riskAssessment.overallSeverity === 'none' || riskAssessment.issues.length === 0) {
        // 問題なし、直接投稿
        await performSubmit();
        return;
      }

      // 問題がある場合、プリフライトダイアログを表示
      setPreflightIssues(riskAssessment.issues);
      setPreflightDialogOpen(true);
    } catch (err: any) {
      console.error('プリフライトチェックエラー:', err);
      // エラーの場合は直接投稿
      await performSubmit();
    }
  };

  const performSubmit = async () => {
    if (!content.trim() || !user || !company) return;
    // 警告ダイアログの「投稿する」を連打された場合もここで止める。
    if (submittingRef.current) return;

    // 文字数上限チェック
    const maxLength = selectedPostType === 'voice' ? 2000 : 500;
    if (content.length > maxLength) {
      setError(`文字数が上限（${maxLength}文字）を超えています。`);
      return;
    }

    // バリデーション強化
    if (!company.id) {
      console.error('会社IDが取得できません:', { user, company });
      setError('会社情報が取得できません。ページを再読み込みしてお試しください。');
      return;
    }

    // テーマボックス選択時のテーマID検証
    if (selectedPostType === 'theme' && !activeTheme) {
      setError('現在アクティブなテーマがありません。管理者にテーマの設定を依頼してください。');
      return;
    }

    updateSubmitting(true);
    setError(null);
    setSubmissionError(null);
    setSubmissionProgress(0);
    setIsSubmissionStarted(false);
    isCancelledRef.current = false;
    setSubmissionDialogOpen(true);
    if (onSubmissionStateChange) {
      onSubmissionStateChange(true, 0, null, content, false);
    }

    const startTime = Date.now();
    const minDisplayTime = 20000; // 20秒

    // プログレスバーを20秒で100%にする
    const progressInterval = setInterval(() => {
      if (isCancelledRef.current) {
        clearInterval(progressInterval);
        return;
      }
      // 投稿送信処理が完了している場合は、100%で固定
      if (isSubmissionStarted && submissionProgress >= 99) {
        return;
      }
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / minDisplayTime) * 100, 100);
      setSubmissionProgress(progress);
      if (onSubmissionStateChange) {
        onSubmissionStateChange(true, progress, null, content, isSubmissionStarted);
      }
    }, 100);
    progressIntervalRef.current = progressInterval;

    try {
      // 投稿送信処理を実行するタイミング（19秒経過後、100%になる直前）
      const submissionTime = 19000; // 19秒
      
      // 19秒経過するまで待つ（途中でキャンセルチェック）
      const elapsed = Date.now() - startTime;
      if (elapsed < submissionTime) {
        const remainingTime = submissionTime - elapsed;
        // 50msごとにキャンセルチェック（より頻繁にチェック）
        const checkInterval = 50;
        const checks = Math.ceil(remainingTime / checkInterval);
        for (let i = 0; i < checks; i++) {
          if (isCancelledRef.current) {
            clearInterval(progressInterval);
            return;
          }
          await new Promise(resolve => setTimeout(resolve, Math.min(checkInterval, remainingTime - i * checkInterval)));
        }
      }

      // キャンセルチェック（投稿送信直前 - 1回目）
      if (isCancelledRef.current) {
        clearInterval(progressInterval);
        return;
      }

      // 投稿送信処理の直前にも短い遅延を入れて、最後のキャンセルチャンスを確保
      await new Promise(resolve => setTimeout(resolve, 200));

      // キャンセルチェック（投稿送信直前 - 2回目：遅延後）
      if (isCancelledRef.current) {
        clearInterval(progressInterval);
        return;
      }

      // Flutter版と同じデータ構造で投稿作成
      const postData = {
        anonymousId: user.uid,
        type: selectedPostType as 'voice' | 'thanks' | 'theme',
        category: 'general',
        content: content,
        tags: [],
        urgency: 'medium' as 'low' | 'medium' | 'high',
        themeId: selectedPostType === 'theme' ? activeTheme?.themeId : undefined,
        aiAnalysis: {
          sentiment: 0.0,
          emotions: {
            joy: 0.0,
            anger: 0.0,
            fear: 0.0,
            sadness: 0.0,
            surprise: 0.0,
          },
          toxicity: 0.0,
          personalInfoDetected: false,
          complianceIssues: [],
        },
        statistics: {
          views: 0,
          likes: 0,
          shares: 0,
        },
        attachments: [],
      };

      // キャンセルチェック（投稿送信直前 - 3回目：データ準備後）
      if (isCancelledRef.current) {
        clearInterval(progressInterval);
        return;
      }

      // 投稿送信処理が開始されたことを示す（キャンセルボタンを無効化）
      setIsSubmissionStarted(true);
      // 投稿送信開始を親に通知（キャンセルボタンを無効化するため）
      if (onSubmissionStateChange) {
        onSubmissionStateChange(true, submissionProgress || 0, null, content, true);
      }

      // 投稿送信処理（100%になる直前のタイミングで実行）
      await PostService.createPostWithCompany(postData, company.id);
      
      // プログレスバーの更新を停止して100%に固定
      clearInterval(progressInterval);
      setSubmissionProgress(100);
      if (onSubmissionStateChange) {
        onSubmissionStateChange(true, 100, null, content, true);
      }
      
      // 残り2秒を待つ（100%になるまで）
      const elapsedAfterSubmission = Date.now() - startTime;
      if (elapsedAfterSubmission < minDisplayTime) {
        const remainingTime = minDisplayTime - elapsedAfterSubmission;
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      // 成功メッセージを表示
      const message = selectedPostType === 'thanks' 
        ? '投稿が完了しました。承認後に公開されます。'
        : 'ご意見をいただき、ありがとうございます。投稿が完了しました。';
      setSnackbarMessage(message);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // 親にスナックバーの状態を通知（PC表示でモーダルが閉じた後も表示するため）
      if (onSnackbarChange) {
        onSnackbarChange(true, message, 'success');
      }
      
      // ダイアログを閉じて完了処理
      // スナックバーが表示されるのを待ってから完了処理を実行
      setTimeout(() => {
        setSubmissionDialogOpen(false);
        updateSubmitting(false);
        if (onSubmissionStateChange) {
          onSubmissionStateChange(false, 100, null, content, false);
        }
        onPostCreated();
      }, 1000); // スナックバーが表示されるのを待つため、500ms → 1000msに延長
    } catch (err: any) {
      console.error('投稿作成エラー:', err);
      clearInterval(progressInterval);
      const errorMessage = err.message || '投稿の作成に失敗しました';
      setSubmissionError(errorMessage);
      setSubmissionProgress(100);
      if (onSubmissionStateChange) {
        onSubmissionStateChange(true, 100, errorMessage, content, isSubmissionStarted);
      }
      
      // エラー時も最低20秒経過するまで待つ
      const elapsed = Date.now() - startTime;
      if (elapsed < minDisplayTime) {
        await new Promise(resolve => setTimeout(resolve, minDisplayTime - elapsed));
      }
      
      const errorSnackbarMessage = err.message || '投稿の作成に失敗しました';
      setSnackbarMessage(errorSnackbarMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      
      // 親にスナックバーの状態を通知
      if (onSnackbarChange) {
        onSnackbarChange(true, errorSnackbarMessage, 'error');
      }
      
      setTimeout(() => {
        setSubmissionDialogOpen(false);
        updateSubmitting(false);
        if (onSubmissionStateChange) {
          onSubmissionStateChange(false, 100, errorMessage, content, false);
        }
      }, 1000);
    }
  };

  const handleCancelSubmission = useCallback(() => {
    isCancelledRef.current = true;
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    setSubmissionDialogOpen(false);
    updateSubmitting(false);
    setSubmissionProgress(0);
    setSubmissionError(null);
    setIsSubmissionStarted(false);
    if (onSubmissionStateChange) {
      onSubmissionStateChange(false, 0, null, content, false);
    }
    if (onCancelSubmission) {
      onCancelSubmission();
    }
  }, [content, onSubmissionStateChange, onCancelSubmission]);

  // handleCancelSubmissionを親に渡す
  useEffect(() => {
    if (onCancelSubmissionRef) {
      onCancelSubmissionRef.current = handleCancelSubmission;
    }
    return () => {
      if (onCancelSubmissionRef) {
        onCancelSubmissionRef.current = null;
      }
    };
  }, [handleCancelSubmission, onCancelSubmissionRef]);

  const handleAiReview = async () => {
    if (!content.trim()) return;

    setIsReviewing(true);
    setError(null);
    
    try {
      
      const result = await Promise.race([
        aiService.reviewText(content, selectedPostType),
        new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('タイムアウト')), 30000)
        )
      ]);
      
      
      setReviewResult(result);
      setReviewDialogOpen(true);
    } catch (err: any) {
      console.error('AI校閲エラー:', err);
      setError(err.message || 'AI校閲に失敗しました');
    } finally {
      setIsReviewing(false);
    }
  };

  const handleApplyReview = () => {
    if (!reviewResult) return;
    
    const revisedText = reviewResult.revisedText || content;
    const percent = aiService.calculateAdjustmentPercent(content, revisedText);
    
    setContent(revisedText);
    setIsReviewed(true);
    setAiAdjustedPercent(percent);
    setReviewDialogOpen(false);
    
  };

  const handlePreflightAction = (action: 'post' | 'ai' | 'edit') => {
    setPreflightDialogOpen(false);
    
    if (action === 'post') {
      performSubmit();
    } else if (action === 'ai') {
      handleAiReview();
    }
  };

  return (
    <>
      <Box sx={{ p: 2, pb: selectedPostType === 'theme' ? 10 : 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 内容入力フィールド */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={8}
            value={content}
            onChange={(e) => !isSubmitting && setContent(e.target.value)}
            placeholder={getPlaceholderText()}
            variant="outlined"
            disabled={isSubmitting}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: 'white',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: AppColors.primary,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: AppColors.primary,
                  borderWidth: 2,
                },
              },
            }}
          />
        </Box>

        {/* AI校閲ボタン */}
        <Box sx={{ mb: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={isReviewing ? <CircularProgress size={18} /> : <AutoFixHighIcon />}
            onClick={handleAiReview}
            disabled={!content.trim() || isSubmitting || isReviewing}
            sx={{
              height: 48,
              borderColor: AppColors.primary,
              color: AppColors.primary,
              borderRadius: 3,
              '&:hover': {
                borderColor: AppColors.primaryDark,
                bgcolor: `${AppColors.primary}10`,
              },
            }}
          >
            {isReviewing ? '校閲中...' : 'AIで校閲'}
          </Button>
          
          {/* AI校閲済みの状態表示 */}
          {isReviewed && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 16, color: AppColors.success, mr: 1 }} />
              <Typography variant="body2" sx={{ color: AppColors.success, fontWeight: 500 }}>
                AI校閲済み（{Math.round(aiAdjustedPercent * 100)}%調整）
              </Typography>
            </Box>
          )}
        </Box>

        {/* 投稿タイプ選択 */}
        {showPostTypeSelector ? (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {( ['voice', 'thanks', 'theme'] as const ).map((type) => {
                const IconComponent = getPostTypeIcon(type);
                const color = getPostTypeColor(type);
                const isSelected = selectedPostType === type;
                
                return (
                  <Box
                    key={type}
                    onClick={() => {
                      if (isSubmitting) return;
                      // 画面遷移（URL更新）も併せて行いたい場合は親に委譲する
                      onPostTypeChange?.(type);
                      setSelectedPostType(type);
                    }}
                    sx={{
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 2,
                      bgcolor: isSelected ? `${color}20` : '#f5f5f5',
                      border: `2px solid ${isSelected ? color : '#e0e0e0'}`,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      opacity: isSubmitting ? 0.5 : 1,
                      transition: 'all 0.2s',
                      pointerEvents: isSubmitting ? 'none' : 'auto',
                      '&:hover': {
                        bgcolor: isSubmitting ? (isSelected ? `${color}20` : '#f5f5f5') : (isSelected ? `${color}20` : '#f0f0f0'),
                      },
                    }}
                  >
                    <IconComponent
                      sx={{
                        color: isSelected ? color : '#666',
                        fontSize: 24,
                      }}
                    />
                  </Box>
                );
              })}
              <Typography
                sx={{
                  ml: 1.5,
                  fontWeight: 600,
                  color: AppColors.textPrimary,
                }}
              >
                {getSelectedBoxName()}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                }}
              >
                {content.length} / {selectedPostType === 'voice' ? 2000 : 500}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                }}
              >
                {content.length} / {selectedPostType === 'voice' ? 2000 : 500}
              </Typography>
            </Box>
          </Box>
        )}

        {/* テーマボックス選択時のアクティブテーマ表示 */}
        {selectedPostType === 'theme' && (
          <Box sx={{ mb: 2 }}>
            {themeLoading ? (
              <Box sx={{ 
                p: 2, 
                border: `1px solid ${AppColors.border}`, 
                borderRadius: 3, 
                bgcolor: AppColors.surface,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ color: AppColors.textSecondary }}>
                  テーマ情報を読み込み中...
                </Typography>
              </Box>
            ) : activeTheme ? (
              <Box sx={{
                p: 2,
                border: `1px solid ${AppColors.themeBoxColor}`,
                borderRadius: 3,
                bgcolor: `${AppColors.themeBoxColor}10`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <TopicIcon sx={{ color: AppColors.themeBoxColor, mr: 1 }} />
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600, 
                    color: AppColors.textSecondary 
                  }}>
                    現在のテーマ
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold', 
                  color: AppColors.themeBoxColor,
                  mb: 1
                }}>
                  {activeTheme.title}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: AppColors.textPrimary,
                  mb: 1.5,
                  whiteSpace: 'pre-line'
                }}>
                  {activeTheme.description}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarTodayIcon sx={{ fontSize: 16, color: AppColors.textSecondary, mr: 0.5 }} />
                  <Typography variant="body2" sx={{ color: AppColors.textSecondary }}>
                    期限: {activeTheme.endDate.toLocaleDateString('ja-JP')}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{
                p: 2,
                border: `1px solid ${AppColors.warning}`,
                borderRadius: 3,
                bgcolor: `${AppColors.warning}10`,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <WarningIcon sx={{ color: AppColors.warning }} />
                <Typography variant="body2" sx={{ color: AppColors.textPrimary }}>
                  現在アクティブなテーマがありません
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {/* 投稿ボタン用のスペーサー（固定ボタンの高さ分、テーマボックスが表示される場合のみ追加） */}
        {selectedPostType === 'theme' && <Box sx={{ height: 40 }} />}
      </Box>

      {/* 投稿ボタン（画面下に固定） */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          bgcolor: AppColors.surface,
          borderTop: '1px solid #e0e0e0',
          zIndex: 1,
        }}
      >
        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={
            !content.trim() ||
            isSubmitting ||
            isPreparing ||
            isReviewing ||
            (selectedPostType === 'voice' && content.length > 2000) ||
            ((selectedPostType === 'thanks' || selectedPostType === 'theme') && content.length > 500)
          }
          sx={{
            height: 56,
            bgcolor: AppColors.primary,
            color: 'black',
            borderRadius: 3,
            fontSize: 16,
            fontWeight: 600,
            mb: 1.5,
            '&:hover': {
              bgcolor: AppColors.primaryDark,
            },
            '&:disabled': {
              bgcolor: '#e0e0e0',
              color: '#999',
            },
          }}
        >
          {isSubmitting || isPreparing ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            '投稿する'
          )}
        </Button>
        {onCancel && (
          <Button
            fullWidth
            variant="outlined"
            onClick={onCancel}
            disabled={isSubmitting || isReviewing}
            sx={{
              height: 48,
              borderColor: '#e0e0e0',
              color: AppColors.textPrimary,
              borderRadius: 3,
              fontSize: 15,
              fontWeight: 500,
              '&:hover': {
                borderColor: AppColors.textSecondary,
                bgcolor: '#f5f5f5',
              },
              '&:disabled': {
                borderColor: '#e0e0e0',
                color: '#999',
              },
            }}
          >
            ホームへ戻る
          </Button>
        )}
      </Box>

      {/* 校閲結果ダイアログ */}
      <Dialog 
        open={reviewDialogOpen} 
        onClose={() => setReviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>AI校閲結果</DialogTitle>
        <DialogContent>
          {reviewResult && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                補正前
              </Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {content}
                </Typography>
              </Paper>
              
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                補正後
              </Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#f0f8ff' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {reviewResult.revisedText || content}
                </Typography>
              </Paper>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TuneIcon sx={{ fontSize: 16, color: AppColors.textSecondary, mr: 1 }} />
                <Typography variant="body2" sx={{ color: AppColors.textSecondary }}>
                  全体の{Math.round(aiService.calculateAdjustmentPercent(content, reviewResult.revisedText) * 100)}%を調整
                </Typography>
              </Box>
              
              {reviewResult.notes && reviewResult.notes.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    変更理由
                  </Typography>
                  {reviewResult.notes.map((note, index) => (
                    <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                      • {note}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>
            キャンセル
          </Button>
          <Button onClick={handleApplyReview} variant="outlined">
            適用
          </Button>
        </DialogActions>
      </Dialog>

      {/* プリフライトダイアログ */}
      <Dialog 
        open={preflightDialogOpen} 
        onClose={() => setPreflightDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>投稿前チェック</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              以下の問題が検出されました。投稿を続行しますか？
            </Typography>
            
            {preflightIssues.map((issue, index) => (
              <Box key={index} sx={{ mb: 1, p: 1, bgcolor: '#fff3cd', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <WarningIcon sx={{ fontSize: 16, color: '#856404', mr: 1 }} />
                  <Typography variant="subtitle2" sx={{ color: '#856404', fontWeight: 600 }}>
                    {issue.type === 'aggressive_tone' && '攻撃的な表現'}
                    {issue.type === 'discrimination' && '差別的表現'}
                    {issue.type === 'personal_info' && '個人情報'}
                    {issue.type === 'personal_info_phone' && '電話番号'}
                    {issue.type === 'name_or_handle' && '個人名'}
                    {issue.type === 'person_name' && '個人名'}
                    {issue.type === 'punctuation' && '句読点'}
                    {issue.type === 'other' && 'その他'}
                  </Typography>
                  <Chip 
                    label={issue.severity === 'warning' ? '警告' : '注意'} 
                    size="small" 
                    color={issue.severity === 'warning' ? 'error' : 'warning'}
                    sx={{ ml: 1 }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: '#856404' }}>
                  {issue.reason}
                </Typography>
                <Typography variant="caption" sx={{ color: '#856404', fontStyle: 'italic' }}>
                  「{issue.snippet}」
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handlePreflightAction('edit')}>
            編集に戻る
          </Button>
          <Button onClick={() => handlePreflightAction('ai')} variant="outlined">
            AIで校閲
          </Button>
          <Button 
            onClick={() => handlePreflightAction('post')}
            variant="contained"
            disabled={
              isSubmitting ||
              (selectedPostType === 'voice' && content.length > 2000) ||
              ((selectedPostType === 'thanks' || selectedPostType === 'theme') && content.length > 500)
            }
            sx={{ bgcolor: AppColors.primary, color: 'black' }}
          >
            投稿する
          </Button>
        </DialogActions>
      </Dialog>

      {/* 投稿確認ダイアログ */}
      {showSubmissionDialog && (
      <Dialog 
        open={submissionDialogOpen} 
        onClose={() => {}} // 投稿中は閉じられないようにする
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={!submissionError} // エラー時のみESCキーで閉じられる
      >
        <DialogTitle>投稿中</DialogTitle>
        <DialogContent>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              投稿内容を確認してください
            </Typography>
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5', maxHeight: 300, overflow: 'auto' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {content}
              </Typography>
            </Paper>
            
            {submissionError ? (
              <Alert severity="error" sx={{ mb: 0 }}>
                {submissionError}
              </Alert>
            ) : (
              <Box sx={{ mb: 0 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={submissionProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      bgcolor: AppColors.primary,
                    },
                  }}
                />
                <Typography variant="caption" sx={{ mt: 0.5, color: AppColors.textSecondary, display: 'block', textAlign: 'right' }}>
                  あと{Math.max(0, Math.ceil(20 - (submissionProgress / 100 * 20)))}秒
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ pt: 1, pb: 2 }}>
          {submissionError ? (
            <Button onClick={() => {
              setSubmissionDialogOpen(false);
              updateSubmitting(false);
            }}>
              閉じる
            </Button>
          ) : (
            <Button 
              onClick={handleCancelSubmission}
              variant="outlined"
              fullWidth
              disabled={isSubmissionStarted}
              sx={{
                height: 40,
                borderColor: '#d0d0d0',
                color: '#666',
                borderRadius: 2,
                fontSize: 14,
                fontWeight: 400,
                '&:hover': {
                  borderColor: '#b0b0b0',
                  bgcolor: '#f5f5f5',
                },
                '&:disabled': {
                  borderColor: '#e0e0e0',
                  color: '#999',
                },
              }}
            >
              投稿を取り消す
            </Button>
          )}
        </DialogActions>
      </Dialog>
      )}

      {/* スナックバー */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1, // モーダルより上に表示
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            width: 'auto',
            maxWidth: '90%',
            minWidth: 300,
            backgroundColor: snackbarSeverity === 'success' ? AppColors.success : AppColors.error,
            color: 'white',
            fontSize: '1rem',
            padding: '12px 20px',
            '& .MuiAlert-icon': {
              color: 'white',
            },
            '& .MuiAlert-message': {
              fontWeight: 'bold',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

